import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthShell from '../components/AuthShell';
import { Button } from '../components/ui/button';
import PasswordInput from '../components/PasswordInput';
import { apiService } from '../services/api';
import { TERMS_VERSION } from '../legal/versions';

const USE_CASE_OPTIONS = [
  { value: '', label: 'Select a use case…' },
  { value: 'customer_churn', label: 'Predict which customers might churn soon' },
  { value: 'customer_spending', label: 'Predict how much each customer will spend next month' },
  { value: 'predictive_maintenance', label: 'Predictive maintenance: predict which parts or machines might fail soon' },
  { value: 'financial_risk', label: 'Predict risk of financial assets' },
  { value: 'demand_forecasting', label: 'Predict the demand for each product next month' },
  { value: 'other', label: 'Other' },
];

const SignupPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '', use_case: '', company_name: '',
  });
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await apiService.register({ ...form, accepted_terms: accepted, terms_version: TERMS_VERSION });
      navigate('/login', { state: { message: 'Account created successfully. You can now log in.' } });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      topRightCta={{
        label: 'Log in',
        kind: 'ghost',
        onClick: () => navigate('/login'),
      }}
    >
      {success ? (
        <div className="auth-center">
          <h1 className="auth-heading">Check your <span className="auth-heading__accent">inbox</span></h1>
          <p className="auth-subheading">{success}</p>
          <Button className="auth-btn-block" onClick={() => navigate('/login')}>
            Back to log in
          </Button>
        </div>
      ) : (
        <>
          <h1 className="auth-heading">
            Create <span className="auth-heading__accent">account</span>
          </h1>
          <p className="auth-subheading">Start analysing your data in minutes.</p>

          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            <div className="auth-row">
              <div className="auth-field">
                <label className="auth-label" htmlFor="first_name">First name</label>
                <input
                  id="first_name"
                  name="first_name"
                  className="auth-input"
                  placeholder="Marieke"
                  value={form.first_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="auth-field">
                <label className="auth-label" htmlFor="last_name">Last name</label>
                <input
                  id="last_name"
                  name="last_name"
                  className="auth-input"
                  placeholder="de Vries"
                  value={form.last_name}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className="auth-input"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="password">Password</label>
              <PasswordInput
                id="password"
                name="password"
                autoComplete="new-password"
                className="auth-input"
                placeholder="••••••••"
                value={form.password}
                onChange={handleChange}
                required
              />
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="use_case">
                Closest use-case
              </label>
              <select
                id="use_case"
                name="use_case"
                className="auth-select"
                value={form.use_case}
                onChange={handleChange}
                required
              >
                {USE_CASE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="company_name">
                Company name <span className="auth-label__hint">(optional)</span>
              </label>
              <input
                id="company_name"
                name="company_name"
                className="auth-input"
                placeholder="Northbeam Logistics"
                value={form.company_name}
                onChange={handleChange}
              />
            </div>

            {/* Clickwrap: business-use declaration + Terms/AUP acceptance, required
                by the register endpoint and recorded on the profile. */}
            <label className="auth-check" htmlFor="accepted_terms">
              <input
                id="accepted_terms"
                name="accepted_terms"
                type="checkbox"
                checked={accepted}
                onChange={(e) => { setAccepted(e.target.checked); setError(''); }}
                required
              />
              <span>
                I am using Qombra for business purposes and accept the{' '}
                <Link className="auth-link" to="/terms" target="_blank">Terms of Service</Link> and{' '}
                <Link className="auth-link" to="/aup" target="_blank">Acceptable Use Policy</Link>.
                The <Link className="auth-link" to="/privacy" target="_blank">Privacy Policy</Link>{' '}
                explains how we process personal data.
              </span>
            </label>

            {error && <p className="auth-error">{error}</p>}

            <div className="auth-submit">
              <Button type="submit" className="auth-btn-block" disabled={loading || !accepted}>
                {loading ? 'Creating account…' : 'Create account'}
              </Button>
            </div>
          </form>

          <p className="auth-aside">
            Already have an account?{' '}
            <button type="button" className="auth-link" onClick={() => navigate('/login')}>
              Log in
            </button>
          </p>
        </>
      )}
    </AuthShell>
  );
};

export default SignupPage;
